import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { generatePasswordResetLink } from '@shipstation/client-common/auth/generate-password-reset-link';
import { StepUpAuthModal } from '@shipstation/client-common/mfa/components/step-up-auth-modal';
import { PHASES } from '@shipstation/client-common/mfa/mfa-constants';
import { checkIsTokenTimeoutError } from '@shipstation/client-common/mfa/mfa-helpers';
import { mfaMessageSets } from '@shipstation/client-common/mfa/mfa-messages';
import { configureMfaNotifier, configureMfaTracker } from '@shipstation/client-common/mfa/mfa-platform';

import { emptyArray } from 'ui-components/default-props/empty';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';

import { ssUiClientMfaNotifier } from './notifier';
import { setActiveMfaAnalyticsSource, ssUiClientMfaTracker } from './tracker';
import { useCurrentUser } from '../../../hooks/use-current-user';

import type { PhoneVerificationState } from '@shipstation/client-common/mfa/mfa-constants';
import type { MfaMessageSet } from '@shipstation/client-common/mfa/mfa-messages';
import type { MfaConsent, MfaEnrollment } from '@shipstation/client-common/mfa/mfa-types';
import type { MessageDescriptor } from 'react-intl';

configureMfaNotifier(ssUiClientMfaNotifier);
configureMfaTracker(ssUiClientMfaTracker);

/**
 * G6 — load-bearing for `hooks/use-mfa-enrollment.js`,
 * `pages/settings/sign-in-and-security/sections/security/current-enrollments.jsx`, and
 * `components/mfa/mfa-enrollment-modal.jsx`. Do not change this shape.
 */
export type StepUpAuthContextValue = {
  accessToken: string;
  mfaEnrollments: MfaEnrollment[];
  detectAndHandleAuthTokenExpirationError: (error: unknown) => boolean;
};

const noopDetectAndHandleAuthTokenExpirationError = () => false;

export const StepUpAuthContext = createContext<StepUpAuthContextValue>({
  accessToken: '',
  mfaEnrollments: emptyArray as MfaEnrollment[],
  detectAndHandleAuthTokenExpirationError: noopDetectAndHandleAuthTokenExpirationError,
});

export const useStepUpAuthContext = (): StepUpAuthContextValue => useContext(StepUpAuthContext);

export type StepUpAuthContextProviderProps = {
  /** rendered only once step-up auth has completed */
  children?: React.ReactNode;
  /** request url that failed due to mfa required, or a USER_ACTIONS feature name */
  source: string;
  isModalOpen: boolean;
  /** title referencing the workflow gated by this step-up auth */
  stepUpAuthModalTitle?: MessageDescriptor;
  messages?: MfaMessageSet;
  onClose?: () => void;
  onStepUpAuthSuccess?: (accessToken: string) => void;
  /**
   * Determines some of the properties on the token asked for during step-up auth. When false,
   * a token with an updated `last_mfa_completed` claim is requested instead.
   */
  isMfaSettings?: boolean;
  /** hides every close affordance — the user cannot skip this step-up */
  isUnskippable?: boolean;
  phoneVerificationState?: PhoneVerificationState;
  onTokenTimeout?: () => void;
  consents?: MfaConsent[];
  consentDisclaimer?: MessageDescriptor;
  userPhone?: string;
  onResendCode?: () => Promise<void>;
};

/**
 * Adapter replacement for `../index.jsx`, behind the `enableClientCommonStepUpAuth` flag.
 * Owns everything client-common cannot know about: redux-backed username, ss-ui-client
 * z-index stacking, the notifier/tracker seams, and the `VERIFIED`-gated context.
 */
export const StepUpAuthContextProvider = ({
  children,
  source,
  isModalOpen,
  stepUpAuthModalTitle,
  messages = mfaMessageSets.mfaSettingsMessages,
  onClose,
  onStepUpAuthSuccess,
  isMfaSettings = false,
  isUnskippable = false,
  phoneVerificationState,
  onTokenTimeout,
  consents,
  consentDisclaimer,
  userPhone,
  onResendCode,
}: StepUpAuthContextProviderProps) => {
  const [phase, setPhase] = useState<string>(PHASES.TRYING_WITHOUT_PASSWORD);
  const [accessToken, setAccessToken] = useState('');
  const [mfaEnrollments, setMfaEnrollments] = useState<MfaEnrollment[]>(emptyArray as MfaEnrollment[]);
  const [stepUpAttemptCount, setStepUpAttemptCount] = useState(0);

  const currentUser = useCurrentUser();
  const username = currentUser?.username ?? '';
  const zIndex = useMaxZ();

  useEffect(() => {
    setActiveMfaAnalyticsSource(source);
  }, [source]);

  const detectAndHandleAuthTokenExpirationError = useCallback(
    (error: unknown) => {
      // Legacy `index.jsx` read `error?.response?.data || error`, so a bare
      // `{ code: 6000 }` also matched. `checkIsTokenTimeoutError` only inspects
      // `response.data`, so wrap bare errors to keep both shapes matching.
      const hasResponseData = Boolean((error as { response?: { data?: unknown } })?.response?.data);
      const isTokenTimeout = checkIsTokenTimeoutError(hasResponseData ? error : { response: { data: error } });

      if (!isTokenTimeout) {
        return false;
      }

      setAccessToken('');
      setPhase(PHASES.TRYING_WITHOUT_PASSWORD);
      setStepUpAttemptCount((previousCount) => previousCount + 1);
      onTokenTimeout?.();

      return true;
    },
    [onTokenTimeout],
  );

  const contextValue = useMemo(() => {
    return {
      accessToken,
      detectAndHandleAuthTokenExpirationError,
      mfaEnrollments,
    };
  }, [accessToken, detectAndHandleAuthTokenExpirationError, mfaEnrollments]);

  const onVerified = useCallback(
    (verifiedAccessToken: string) => {
      setAccessToken(verifiedAccessToken);
      setPhase(PHASES.VERIFIED);
      onStepUpAuthSuccess?.(verifiedAccessToken);
    },
    [onStepUpAuthSuccess],
  );

  const onCloseModal = useCallback(() => {
    if (isUnskippable) {
      return;
    }
    onClose?.();
    setPhase(PHASES.TRYING_WITHOUT_PASSWORD);
    setStepUpAttemptCount((previousCount) => previousCount + 1);
  }, [isUnskippable, onClose]);

  const isVerified = phase === PHASES.VERIFIED;
  const shouldShowModal = isModalOpen && !isVerified;

  return (
    <>
      {/* G6 — `onEnrollmentsLoaded` is not yet part of `StepUpAuthModalProps`; the modal keeps
          `enrollments` internal, but `current-enrollments.jsx` seeds its table from the context
          and never refetches, so the enrollments have to reach `StepUpAuthContextValue`. */}
      {shouldShowModal ? (
        <StepUpAuthModal
          consentDisclaimer={consentDisclaimer}
          consents={consents}
          isMfaSettings={isMfaSettings}
          isOpen={shouldShowModal}
          isUnskippable={isUnskippable}
          key={stepUpAttemptCount}
          messageSet={messages}
          onClose={onCloseModal}
          onEnrollmentsLoaded={setMfaEnrollments}
          onResendCode={onResendCode}
          onTokenTimeout={onTokenTimeout}
          onVerified={onVerified}
          passwordResetHref={generatePasswordResetLink(username)}
          phoneVerificationState={phoneVerificationState}
          source={source}
          title={stepUpAuthModalTitle}
          userPhone={userPhone}
          username={username}
          zIndex={zIndex}
        />
      ) : null}
      {isVerified ? <StepUpAuthContext.Provider value={contextValue}>{children}</StepUpAuthContext.Provider> : null}
    </>
  );
};
